// Admin → RF Devices. Every sensor with no unique number, estate-wide, plus
// live learning and the event log. Both shells route here.
//
// The per-receiver view lives on the RF receiver's own screen (DeviceID 47);
// this one answers the other question — "what RF hardware exists at all", which
// is the one you ask when a notification arrives from a sensor nobody named.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, AppBar, Toolbar, IconButton, Alert, TextField, MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useApp } from '../../../core/state/AppContext';
import RfDevicesPanel from '../../shared/RfDevicesPanel';

// ⚠️ NOT ADMIN-ONLY, AND DELIBERATELY SO. A homeowner must be able to add the
// safe-locker sensor they just stuck to the door, from the phone in their hand.
// What a non-admin may see is scoped instead: the location picker offers only
// THEIR locations and has no "every location" entry, so the page answers "my RF
// sensors" rather than the estate's.
export default function RfDevicesScreen() {
  const { isSuperAdmin, locations, locationId: current } = useApp();
  const navigate = useNavigate();
  const [locId, setLocId] = useState('');

  // Default to the location in use, so the page opens on what the operator is
  // already looking at rather than on 117 sites.
  useEffect(() => { if (current && !locId) setLocId(current); }, [current, locId]);

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" elevation={2}>
        <Toolbar variant="dense">
          <IconButton edge="start" color="inherit" onClick={() => navigate('/home')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, fontSize: 16 }}>RF Devices</Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          select size="small" label="Location" value={locId}
          onChange={(e) => setLocId(e.target.value)}
          sx={{ width: { xs: '100%', sm: 280 } }}
        >
          {isSuperAdmin && <MenuItem value="">Every location</MenuItem>}
          {(locations || []).map((l) => (
            <MenuItem key={l.LocationID} value={l.LocationID}>
              {l.LocationWithName || l.LocationName || `Location ${l.LocationID}`}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="text.secondary">
          A door, safe, fire or motion sensor has no unique number — it is known by its
          RF code. The top 24 bits are the sensor; the last 8 are the event.
        </Typography>
      </Box>

      <Box sx={{ px: 1, pb: 1, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {/* A non-admin with no location chosen yet would otherwise ask for the
            whole estate, so the panel waits for one. */}
        {!isSuperAdmin && !locId
          ? <Alert severity="info">Pick a location to see its RF sensors.</Alert>
          : <RfDevicesPanel locationId={Number(locId) || 0} />}
      </Box>
    </Box>
  );
}
